import { z } from '@dcp/shared'

/**
 * Maximum stored length of a connection name.
 *
 * `ledgersync_connections.name` is PostgreSQL `TEXT` (unbounded), so nothing in
 * the database refuses an oversized paste; capping at 255 mirrors the pack and
 * facility forms and turns it into a precise field error instead of a silently
 * stored wall of text that the Bank column would then have to truncate
 * everywhere.
 *
 * EXPORTED because the data layer enforces the SAME bound (`db-tenant.ts` →
 * `ledgerSyncConnections.rename`): this form is one door to that write, not the
 * only one, and a cap that lives only in the dialog is a cap every other caller
 * walks past. Shared as a constant rather than retyped so the two cannot drift.
 */
export const MAX_CONNECTION_NAME_LENGTH = 255

/**
 * Zod schema for the Rename Connection dialog (DCP-324, AC#5).
 *
 * One required field. `.trim().min(1)` rejects a whitespace-only name for the
 * same reason the data layer does: a blank name would leave the connection row
 * with no visible label, because the read model's `display_name` only falls back
 * to the institution name when `name` is NULL — not when it is the string `" "`.
 */
export const connectionRenameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Connection name is required')
    .max(
      MAX_CONNECTION_NAME_LENGTH,
      `Name must be ${MAX_CONNECTION_NAME_LENGTH} characters or fewer`,
    ),
})

/** Form shape inferred from {@link connectionRenameSchema}. */
export type ConnectionRenameFormData = z.infer<typeof connectionRenameSchema>
